/**
 * MfaEnrollmentService - Firebase multi-factor enrollment orchestrator
 *
 * Shared by the My Account security page (opt-in MFA) and the forced
 * enrollment page (/enroll-mfa). Wraps the Firebase multiFactor() enrollment
 * flows for SMS (phone) and TOTP (authenticator app) second factors.
 *
 * Boundary: Does NOT manage reCAPTCHA lifecycles — RecaptchaVerifier is
 * DOM-bound, so components own its creation/cleanup and pass it in as an
 * ApplicationVerifier parameter. Does NOT navigate or render messages.
 *
 * External dependencies:
 * - Firebase Auth: multiFactor sessions, PhoneAuthProvider, TOTP generators
 * - FirebaseInjectionHelperService: Auth instance access
 */
import { Injectable, inject } from '@angular/core';
import {
  ApplicationVerifier,
  MultiFactorInfo,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  TotpSecret,
  User as FirebaseUser,
  multiFactor,
} from 'firebase/auth';
import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';

/**
 * Result of starting a TOTP enrollment: the generated secret (exposes
 * `secretKey` for manual entry) and the otpauth QR URL for authenticator apps.
 */
export interface TotpEnrollmentStart {
  /** Firebase TOTP secret; keep in memory only until enrollment confirms. */
  secret: TotpSecret;
  /** QR code URL (otpauth) generated for "Scan Ninja Inc". */
  qrUrl: string;
}

/**
 * Stateful (per in-flight enrollment) service that runs the two-step
 * phone/TOTP enrollment handshakes. Call the matching start* method first;
 * the confirm* method completes the enrollment using the stashed state.
 *
 * Side effects:
 * - Firebase Auth: sends SMS codes, enrolls/unenrolls second factors
 * - In-memory pending state: verificationId / TotpSecret between start and confirm
 */
@Injectable({ providedIn: 'root' })
export class MfaEnrollmentService {
  private firebaseHelper = inject(FirebaseInjectionHelperService);

  /** User whose phone enrollment is in flight (set by startPhoneEnrollment). */
  private pendingPhoneUser: FirebaseUser | null = null;
  /** SMS verification ID returned by verifyPhoneNumber. */
  private pendingVerificationId = '';
  /** E.164 phone number used to build the default factor display name. */
  private pendingPhoneNumber = '';

  /** User whose TOTP enrollment is in flight (set by startTotpEnrollment). */
  private pendingTotpUser: FirebaseUser | null = null;
  /** Generated TOTP secret awaiting confirmation. */
  private pendingTotpSecret: TotpSecret | null = null;

  /**
   * Returns the currently enrolled second factors for a user.
   *
   * @param user - Firebase Auth user to inspect
   * @returns Array of enrolled MultiFactorInfo (empty when MFA is not set up)
   * - Idempotency: Safe; reads in-memory Firebase user state only
   */
  loadStatus(user: FirebaseUser): MultiFactorInfo[] {
    return multiFactor(user).enrolledFactors;
  }

  /**
   * Starts SMS second-factor enrollment: opens a multi-factor session and
   * sends a verification code to the given phone number.
   *
   * @param user - Signed-in Firebase user enrolling the factor
   * @param phoneNumber - Destination phone number in E.164 format (e.g. +15551234567)
   * @param recaptchaVerifier - DOM-bound ApplicationVerifier owned by the calling component
   * @returns Promise resolving to the SMS verification ID
   * - Errors: Rethrows Firebase errors (e.g. auth/invalid-phone-number,
   *   auth/requires-recent-login) for the component to surface
   * - State touched: pending phone enrollment state (user, verificationId, number)
   */
  async startPhoneEnrollment(
    user: FirebaseUser,
    phoneNumber: string,
    recaptchaVerifier: ApplicationVerifier,
  ): Promise<string> {
    const auth = this.firebaseHelper.getAuth();
    const multiFactorSession = await multiFactor(user).getSession();
    const phoneInfoOptions = {
      phoneNumber,
      session: multiFactorSession,
    };

    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      recaptchaVerifier,
    );

    this.pendingPhoneUser = user;
    this.pendingVerificationId = verificationId;
    this.pendingPhoneNumber = phoneNumber;
    return verificationId;
  }

  /**
   * Completes SMS enrollment with the code the user received.
   *
   * @param code - 6-digit SMS verification code
   * @param displayName - Optional factor label; defaults to "Phone: <number>"
   * @returns Promise resolving when the factor is enrolled
   * - Errors: Throws when no enrollment is in flight; rethrows Firebase errors
   *   (e.g. auth/invalid-verification-code) for the component to surface
   * - State touched: clears pending phone enrollment state on success
   */
  async confirmPhoneCode(code: string, displayName?: string): Promise<void> {
    if (!this.pendingPhoneUser || !this.pendingVerificationId) {
      throw new Error('No phone enrollment in progress');
    }

    const cred = PhoneAuthProvider.credential(this.pendingVerificationId, code);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await multiFactor(this.pendingPhoneUser).enroll(
      multiFactorAssertion,
      displayName ?? `Phone: ${this.pendingPhoneNumber}`,
    );

    this.clearPendingPhoneEnrollment();
  }

  /**
   * Starts TOTP (authenticator app) enrollment: generates a shared secret and
   * the QR code URL to scan.
   *
   * @param user - Signed-in Firebase user enrolling the factor
   * @returns Promise resolving to the TotpSecret and its QR code URL
   * - Errors: Rethrows Firebase errors (e.g. auth/requires-recent-login)
   * - State touched: pending TOTP enrollment state (user, secret)
   */
  async startTotpEnrollment(user: FirebaseUser): Promise<TotpEnrollmentStart> {
    const multiFactorSession = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(
      multiFactorSession,
    );

    // Generate QR code URL for authenticator apps
    const accountName = user.email || 'Scan Ninja User';
    const qrUrl = secret.generateQrCodeUrl(accountName, 'Scan Ninja Inc');

    this.pendingTotpUser = user;
    this.pendingTotpSecret = secret;
    return { secret, qrUrl };
  }

  /**
   * Completes TOTP enrollment with a code from the authenticator app.
   *
   * @param code - 6-digit TOTP code
   * @param displayName - Optional factor label; defaults to "Authenticator App"
   * @returns Promise resolving when the factor is enrolled
   * - Errors: Throws when no enrollment is in flight; rethrows Firebase errors
   *   (e.g. auth/invalid-verification-code) for the component to surface
   * - State touched: clears pending TOTP enrollment state on success
   */
  async confirmTotpCode(code: string, displayName?: string): Promise<void> {
    if (!this.pendingTotpUser || !this.pendingTotpSecret) {
      throw new Error('No authenticator enrollment in progress');
    }

    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
      this.pendingTotpSecret,
      code,
    );
    await multiFactor(this.pendingTotpUser).enroll(
      multiFactorAssertion,
      displayName ?? 'Authenticator App',
    );

    this.clearPendingTotpEnrollment();
  }

  /**
   * Removes an enrolled second factor from the user's account.
   *
   * @param user - Signed-in Firebase user
   * @param factorUid - UID of the enrolled factor to remove
   * @returns Promise resolving when unenrolled; resolves as a no-op when the
   *   factor UID is not found (mirrors previous security-page behavior)
   * - Errors: Rethrows Firebase errors (e.g. auth/requires-recent-login)
   */
  async unenroll(user: FirebaseUser, factorUid: string): Promise<void> {
    const enrolledFactor = multiFactor(user).enrolledFactors.find(
      (factor) => factor.uid === factorUid,
    );
    if (!enrolledFactor) {
      return;
    }
    await multiFactor(user).unenroll(enrolledFactor);
  }

  /**
   * Discards any in-flight enrollment state (both phone and TOTP). Call when
   * the user cancels an enrollment flow or the owning component is destroyed.
   */
  cancelEnrollment(): void {
    this.clearPendingPhoneEnrollment();
    this.clearPendingTotpEnrollment();
  }

  /** Clears the pending phone enrollment handshake state. */
  private clearPendingPhoneEnrollment(): void {
    this.pendingPhoneUser = null;
    this.pendingVerificationId = '';
    this.pendingPhoneNumber = '';
  }

  /** Clears the pending TOTP enrollment handshake state. */
  private clearPendingTotpEnrollment(): void {
    this.pendingTotpUser = null;
    this.pendingTotpSecret = null;
  }
}
